import React, { useEffect, useRef, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import Pagination from './components/Pagination'
import clsx from 'clsx'
import OptionsRow from './components/OptionsRow'
import { isEmpty, isNil } from 'ramda'
import TableHeader from './components/TableHeader'
import SearchInput from '../SearchInput/SearchInput'
import useSearch from '../../hooks/useSearch'
import { Column, Table as TableRV } from 'react-virtualized'
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer'
import 'react-virtualized/styles.css'
import useScreen from '../../hooks/useScreen'
import MobileTable from './components/MobileTable'
import NotFoundResults from './components/NotFoundResults'
import { useCallback } from 'react'
import { RenderSortButton } from './utils/renderHelpers'
import {
    formattedRows,
    getCellSize,
    itemSelected,
    onSelectRow,
    renderSelect,
    toggleSorting,
} from './utils/tableUtils'

/** Stable fallback so default empty arrays are not a new reference every render (breaks useEffect deps). */
const EMPTY_ARRAY = []

const Table = props => {
    const {
        columns,
        data,
        setRowsSize,
        totalResults,
        pageSize,
        selectable = false,
        selectedItems: selectedItemsProp,
        onSelectItem,
        onRowClick,
        actions: actionsProp,
        hasOptions,
        options,
        type,
        hasSearch,
        headerTitle,
        headerSubtitle = '',
        searchPlaceholder,
        isInCard = false,
        isDashboardPage = false,
        searchFields,
        singleSelection = true,
        hasActiveFilters = false,
        fitContentHeight = false,
    } = props

    const selectedItems = selectedItemsProp ?? EMPTY_ARRAY
    const actions = actionsProp ?? EMPTY_ARRAY

    const isScrollable = type === 'scroll'

    const [cols, setCols] = useState(
        columns?.map(col => ({ ...col, sorting: 'none' })),
    )
    const [openOption, setOpenOption] = useState(null)
    const [rows, setRows] = useState([])
    const { isMobile, isTablet } = useScreen()
    const previousDataRef = useRef([])
    const ref = useRef(null)

    const {
        query,
        setQuery,
        handleSearchQuery,
        results,
        setResults,
    } = useSearch('', rows, null, searchFields, setRowsSize)

    const [calculatedPageSize, setCalculatedPageSize] = useState(undefined)
    const [heightAvailable, setHeightAvailable] = useState(undefined)

    // Support search while maintaining offline functionality
    const dataToShow = useMemo(() => {
        if (!isEmpty(query)) {
            return results
        }

        return rows
    }, [query, results, rows])

    const dataToShowRef = useRef(dataToShow)
    dataToShowRef.current = dataToShow

    useEffect(() => {
        // Call search function when query changes
        handleSearchQuery()

        if (isEmpty(query)) {
            setOpenOption(null)
        }
    }, [query])

    const calculateHeight = useCallback(() => {
        setHeightAvailable(undefined)
        setCalculatedPageSize(undefined)
        if (ref?.current) {
            setHeightAvailable(ref?.current?.clientHeight)
        }
    }, [ref])

    useEffect(() => {
        calculateHeight()
    }, [ref])

    useEffect(() => {
        window.addEventListener('resize', () => {
            calculateHeight()
        })
        return () => {
            window.removeEventListener('resize', calculateHeight)
        }
    }, [])

    useEffect(() => {
        if (pageSize != null && pageSize !== '') {
            return
        }
        // When height is still 0 (flex layout not measured yet), `heightAvailable &&` would skip
        // and `perPage` stayed undefined — the virtualized body never mounted. Use a minimum of 1.
        if (heightAvailable != null) {
            const size = heightAvailable - 52 - 32 // 52 is the size of td and 32 is the paddings size
            const rowPerPage = Math.floor(size / 64) // 64 is the size of each tr
            setCalculatedPageSize(rowPerPage < 1 ? 1 : rowPerPage)
        }
    }, [heightAvailable, pageSize])

    useEffect(() => {
        const hasSelect = cols?.find(col => col?.field === 'select')

        const newData = formattedRows({
            data:
                data.length > 0 || hasActiveFilters
                    ? data
                    : previousDataRef.current,
            isMobile,
            isTablet,
            options,
            openOption,
            setOpenOption,
        })

        if (selectable) {
            if (!hasSelect) {
                setCols([{ field: 'select', value: '' }, ...cols])
            }

            const updatedRows = newData?.map(row => ({
                ...row,
                select: renderSelect(
                    row?.customId,
                    row?.salesforceID,
                    selectedItems,
                    onSelectItem,
                    isInCard,
                    isMobile,
                    isTablet,
                    () => dataToShowRef.current,
                    singleSelection,
                ),
            }))
            setRows(updatedRows)
            if (!isEmpty(query)) {
                setResults(updatedRows)
            }
        } else {
            setCols(cols.filter(col => col.field !== 'select'))
            const updatedRows = newData?.map(row => ({ ...row, select: null }))
            setRows(updatedRows)
            if (!isEmpty(query)) {
                setResults(updatedRows)
            }

            if (selectedItems?.length > 0) {
                onSelectItem([])
            }
        }
    }, [selectable, selectedItems, data, isMobile, isTablet, openOption, query])

    useEffect(() => {
        if (hasOptions && rows?.length > 0) {
            const hasOptionsCol = cols?.find(col => col?.field === 'options')

            if (!hasOptionsCol) {
                const newCols = [...cols, { field: 'options', value: '' }]
                setCols(newCols)
            }
        }
    }, [hasOptions, rows])

    useEffect(() => {
        const hasOptionsCol = cols?.find(col => col?.field === 'options')

        if (hasOptions && hasOptionsCol) {
            const getNewData = data =>
                data?.map((row, index) => ({
                    ...row,
                    options: (
                        <OptionsRow
                            activeOption={openOption}
                            setActiveOption={setOpenOption}
                            rowData={row}
                            options={options}
                            isLast={
                                isMobile || isTablet
                                    ? false
                                    : index === data?.length - 1
                            }
                        />
                    ),
                }))

            if (query) {
                setResults(getNewData(results))
            } else {
                setRows(getNewData(rows))
            }
        }
    }, [openOption, hasOptions, cols])

    const perPage = pageSize || calculatedPageSize

    const handleRowSelection = customId => {
        onSelectRow({
            customId,
            selectedItems,
            onSelectItem,
            rows: dataToShow,
            singleSelection,
        })
    }

    if (isMobile || isTablet) {
        return (
            <MobileTable
                headerTitle={headerTitle}
                headerSubtitle={headerSubtitle}
                headerActions={[
                    (hasSearch && (
                        <SearchInput
                            key="searchData"
                            placeholder={searchPlaceholder}
                            value={query}
                            setValue={setQuery}
                            name="searchData"
                            additionalClassName="lg:w-64 lg:flex-grow-0 md:w-full md:flex-grow w-full"
                        />
                    )) ||
                        null,
                    ...actions,
                ]}
                data={dataToShow}
                cols={cols}
                selectedItems={selectedItems}
                query={query}
                isDashboardPage={isDashboardPage}
                onSelectCard={handleRowSelection}
                selectable={selectable}
            />
        )
    }

    const handleToggleSorting = columnField => {
        toggleSorting({
            columnField,
            cols,
            setCols,
            isInCard,
            data,
            query,
            results,
            rows,
            setRows,
            setResults,
            options,
            openOption,
            setOpenOption,
            selectedItems,
            onSelectItem,
            isMobile,
            isTablet,
            getRows: () => dataToShowRef.current,
            singleSelection,
        })
    }

    return (
        <div
            className={clsx(
                'overflow-x-auto h-full w-full',
                isInCard && 'overflow-hidden',
            )}
        >
            <div className="inline-block min-w-full h-full align-middle">
                <div
                    style={{ height: !perPage ? '100%' : 'auto' }}
                    className={clsx(
                        'overflow-hidden transition-all max-w-full flex flex-col justify-between',
                        !isInCard
                            ? 'rounded-3xl border border-grayLines'
                            : 'rounded-none rounded-br-3xl rounded-bl-3xl',
                        perPage && 'bg-white',
                    )}
                >
                    <TableHeader
                        isInCard={isInCard}
                        title={headerTitle}
                        subtitle={headerSubtitle}
                        actions={[
                            hasSearch && (
                                <SearchInput
                                    key="searchData"
                                    placeholder={searchPlaceholder}
                                    value={query}
                                    setValue={setQuery}
                                    name="searchData"
                                    additionalClassName="w-64"
                                />
                            ),
                            actions && [...actions],
                        ]}
                    />
                    <div
                        className={clsx(
                            'w-full transition-all',
                            isScrollable
                                ? 'overflow-auto overflow-x-hidden'
                                : 'overflow-visible',
                            isScrollable && perPage && 'bg-white',
                        )}
                        ref={ref}
                        style={{
                            height: !perPage
                                ? '100%'
                                : dataToShow?.length > 0
                                ? dataToShow?.length * 78 + 48
                                : hasActiveFilters
                                ? 170
                                : previousDataRef.current.length > 0
                                ? previousDataRef.current.length * 78 + 48
                                : 'auto',
                            maxHeight: fitContentHeight
                                ? undefined
                                : heightAvailable,
                        }}
                    >
                        {perPage && dataToShow?.length > 0 ? (
                            <AutoSizer defaultHeight={heightAvailable}>
                                {({ width, height }) => {
                                    return (
                                        <TableRV
                                            width={width}
                                            height={height}
                                            headerHeight={48}
                                            rowHeight={78}
                                            rowCount={dataToShow?.length}
                                            headerRowRenderer={({
                                                columns,
                                                ...props
                                            }) => {
                                                return (
                                                    <div
                                                        {...props}
                                                        className="sticky top-0 flex p-0 pr-0 items-center justify-start"
                                                    >
                                                        {columns?.map(item => {
                                                            const columnData = cols?.find(
                                                                col =>
                                                                    col?.field ===
                                                                    item?.props
                                                                        ?.id,
                                                            )

                                                            const itemField =
                                                                item?.props?.id

                                                            return (
                                                                <div
                                                                    style={
                                                                        item
                                                                            ?.props
                                                                            ?.style
                                                                    }
                                                                    key={
                                                                        item?.key
                                                                    }
                                                                    className={clsx(
                                                                        'transition-all bg-grayBackground w-auto px-3 m-0 h-12 capitalize border-b border-t border-grayLines top-0 flex-grow',
                                                                        'flex items-center max-w-full truncate select-none justify-start text-left text-sm group font-normal text-textDarkerGray z-50',
                                                                        !isNil(
                                                                            columnData?.sorting,
                                                                        ) &&
                                                                            columnData?.sorting !=
                                                                                'none' &&
                                                                            'opacity-60',
                                                                        itemField !==
                                                                            'select' &&
                                                                            itemField !==
                                                                                'options' &&
                                                                            'cursor-pointer',
                                                                    )}
                                                                    onClick={() =>
                                                                        handleToggleSorting(
                                                                            itemField,
                                                                        )
                                                                    }
                                                                >
                                                                    {
                                                                        item
                                                                            ?.props
                                                                            ?.children
                                                                    }
                                                                    <div className="relative h-full w-auto">
                                                                        {RenderSortButton(
                                                                            itemField,
                                                                            columnData,
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                )
                                            }}
                                            rowGetter={({ index }) => {
                                                return dataToShow[index]
                                            }}
                                            rowClassName="border-grayLines overflow-visible transition-all"
                                            rowRenderer={({
                                                columns,
                                                style,
                                                key,
                                                className,
                                                rowData,
                                                index,
                                            }) => {
                                                const isSelected = itemSelected(
                                                    selectedItems,
                                                    rowData,
                                                )

                                                return (
                                                    <div
                                                        onClick={
                                                            onRowClick
                                                                ? () =>
                                                                      handleRowSelection(
                                                                          rowData?.customId,
                                                                      )
                                                                : () => null
                                                        }
                                                        style={style}
                                                        key={key}
                                                        className={clsx(
                                                            className,
                                                            index + 1 ===
                                                                dataToShow?.length
                                                                ? 'border-b-0'
                                                                : 'border-b',
                                                            isSelected &&
                                                                `bg-yellow bg-opacity-5`,
                                                        )}
                                                    >
                                                        {columns}
                                                    </div>
                                                )
                                            }}
                                        >
                                            {cols?.map(item => {
                                                // that is for fix the location because the location cell it is very big
                                                const size = getCellSize({
                                                    width,
                                                    item,
                                                    cols,
                                                })
                                                return (
                                                    <Column
                                                        cellRenderer={({
                                                            cellData,
                                                        }) => cellData}
                                                        key={item?.field}
                                                        label={item?.value}
                                                        dataKey={item?.field}
                                                        height={48}
                                                        width={size}
                                                        className={
                                                            item?.field ===
                                                            'options'
                                                                ? 'overflow-visible'
                                                                : 'overflow-hidden'
                                                        }
                                                        id={item?.field}
                                                    />
                                                )
                                            })}
                                        </TableRV>
                                    )
                                }}
                            </AutoSizer>
                        ) : null}
                        {dataToShow?.length === 0 ? (
                            <NotFoundResults cols={cols} query={query} />
                        ) : null}
                    </div>
                    {perPage && !isScrollable ? (
                        <Pagination
                            totalResults={totalResults}
                            pageSize={perPage}
                            {...props}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    )
}

Table.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        }),
    ).isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    setRowsSize: PropTypes.func,
    totalResults: PropTypes.number,
    pageSize: PropTypes.number,
    onChangePage: PropTypes.func,
    selectable: PropTypes.bool,
    selectedItems: PropTypes.arrayOf(PropTypes.object),
    onSelectItem: PropTypes.func,
    onRowClick: PropTypes.func,
    actions: PropTypes.array,
    hasOptions: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.object),
    type: PropTypes.string,
    hasSearch: PropTypes.bool,
    headerTitle: PropTypes.string,
    headerSubtitle: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
        PropTypes.any,
    ]),
    searchPlaceholder: PropTypes.string,
    isInCard: PropTypes.bool,
    searchFields: PropTypes.oneOfType([PropTypes.array, PropTypes.any]),
    isLoaded: PropTypes.bool,
    isDashboardPage: PropTypes.bool,
    singleSelection: PropTypes.bool,
    hasActiveFilters: PropTypes.bool,
    fitContentHeight: PropTypes.bool,
}

export default Table
